import { computed, onMounted, ref, unref } from 'vue';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';

const emptyCatalog = () => ({
  imovel: [],
  recibo: [],
  pf: [],
  pj: [],
  representante: [],
});

const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const sectionForParty = party => {
  const role = party.role || party.party_role;
  const kind = party.kind || party.party_kind;
  return `${role}_${kind === 'pessoa_juridica' ? 'pj' : 'pf'}`;
};

const reviewItemComplete = item =>
  item?.status === 'corrigido' ||
  (Array.isArray(item?.checks) &&
    item.checks.length > 0 &&
    item.checks.every(check => check?.saved === true));

export function useSalesReview({ id, mode = 'return', t, notify }) {
  const transaction = ref(null);
  const catalog = ref(emptyCatalog());
  const items = ref([]);
  const loading = ref(false);
  const saving = ref(false);
  const errors = ref({});

  const isCorrection = computed(() => unref(mode) === 'correction');
  const participants = computed(() => transaction.value?.participants || []);
  const reviewComplete = computed(
    () => items.value.length > 0 && items.value.every(reviewItemComplete)
  );
  const canCompose = computed(
    () =>
      !isCorrection.value &&
      transaction.value?.sales_stage === 'entregue' &&
      Boolean(transaction.value?.permissions?.can_request_review)
  );
  const canSubmit = computed(() => {
    if (!isCorrection.value) return canCompose.value;

    return (
      transaction.value?.sales_stage === 'revisao' &&
      Boolean(transaction.value?.permissions?.can_resubmit_review) &&
      reviewComplete.value
    );
  });

  const load = async () => {
    if (!id.value) return null;
    loading.value = true;
    try {
      const [transactionResponse, catalogResponse] = await Promise.all([
        SalesTransactionsAPI.show(id.value),
        SalesTransactionsAPI.reviewCatalog(id.value),
      ]);
      transaction.value = transactionResponse?.data || null;
      catalog.value = {
        ...emptyCatalog(),
        ...(catalogResponse?.data?.catalog || {}),
      };
      items.value = transaction.value?.review?.items || [];
      return transaction.value;
    } catch (error) {
      notify(messageFrom(error, t('SALES.REVIEW.ERRORS.LOAD')));
      return null;
    } finally {
      loading.value = false;
    }
  };

  const optionsFor = (section, targetKey = '') => {
    if (section === 'imovel') return catalog.value.imovel || [];
    if (section === 'recibo') return catalog.value.recibo || [];

    const target = participants.value.find(
      party => String(party.id) === String(targetKey)
    );
    if (target?.relation_type === 'representante') {
      return catalog.value.representante || [];
    }

    if (section.endsWith('_pf')) return catalog.value.pf || [];

    return catalog.value.pj || [];
  };

  const partyOptions = section =>
    participants.value
      .filter(party => sectionForParty(party) === section)
      .map(party => ({ id: String(party.id), name: party.name }));

  const addItem = item => {
    items.value = [...items.value, { ...item, checks: item.checks || [] }];
    errors.value = {};
  };

  const removeItem = index => {
    items.value = items.value.filter((_, itemIndex) => itemIndex !== index);
  };

  const updateItem = (index, patch) => {
    items.value = items.value.map((item, itemIndex) =>
      itemIndex === index ? { ...item, ...patch } : item
    );
  };

  const replaceItems = nextItems => {
    items.value = Array.isArray(nextItems) ? nextItems : [];
    errors.value = {};
  };

  const validate = () => {
    const nextErrors = {};
    if (!items.value.length)
      nextErrors.items = t('SALES.REVIEW.ERRORS.NO_ITEMS');
    if (
      items.value.some(
        item =>
          !item.section || !item.observation?.trim() || !item.checks?.length
      )
    ) {
      nextErrors.items = t('SALES.REVIEW.ERRORS.ITEM');
    }
    errors.value = nextErrors;
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async profile => {
    if (isCorrection.value) {
      if (!reviewComplete.value) {
        notify(t('SALES.REVIEW.ERRORS.FORM'));
        return null;
      }
      saving.value = true;
      try {
        const currentTransaction = profile
          ? transaction.value
          : (await SalesTransactionsAPI.show(id.value))?.data ||
            transaction.value;
        const response = await SalesTransactionsAPI.resubmitReview(
          id.value,
          profile || currentTransaction?.profile || {}
        );
        transaction.value = response?.data || transaction.value;
        notify(t('SALES.REVIEW.ALERTS.RESUBMITTED'));
        return transaction.value;
      } catch (error) {
        notify(messageFrom(error, t('SALES.REVIEW.ERRORS.RESUBMIT')));
        return null;
      } finally {
        saving.value = false;
      }
    }

    if (!validate()) {
      notify(t('SALES.REVIEW.ERRORS.FORM'));
      return null;
    }
    saving.value = true;
    try {
      const response = await SalesTransactionsAPI.requestReview(
        id.value,
        items.value,
        ''
      );
      transaction.value = response?.data || transaction.value;
      notify(t('SALES.REVIEW.ALERTS.REQUESTED'));
      return transaction.value;
    } catch (error) {
      notify(messageFrom(error, t('SALES.REVIEW.ERRORS.REQUEST')));
      return null;
    } finally {
      saving.value = false;
    }
  };

  onMounted(load);

  return {
    addItem,
    canCompose,
    canSubmit,
    catalog,
    errors,
    isCorrection,
    items,
    load,
    loading,
    optionsFor,
    participants,
    partyOptions,
    replaceItems,
    reviewComplete,
    removeItem,
    saving,
    submit,
    transaction,
    updateItem,
    validate,
  };
}
